'use client'

import { useState } from 'react'
import { useHMRCITSAStore } from '@repo/store/useHMRCITSAStore'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { TextEntry } from '@repo/ui/TextEntry'
import type { HmrcMtdItsaCategory, HmrcMtdItsaCategoryMapping, NominalAccount, SourceType } from '../types'
import { LEGACY_FEE_SOURCE_LABEL } from './constants'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControl from '@mui/material/FormControl'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUISelect from '@mui/material/Select'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

type CategoryMappingFormProps = {
  category: HmrcMtdItsaCategory
  mapping: HmrcMtdItsaCategoryMapping | null
  accounts: NominalAccount[]
  onCancel: () => void
  onSaved: () => void | Promise<void>
}

type NominalOption = { value: string; label: string }

// The @repo/ui Drawer renders at zIndex 6000. Both pickers here open a menu that portals to
// document.body: MUI Select's menu defaults to zIndex 1300, react-select's to 1100 — both BELOW
// the drawer, so their options open hidden behind it and can't be clicked. Raise both above the drawer.
const DRAWER_OVERLAY_Z_INDEX = 7000

// Create/edit form for a single HmrcMtdItsaCategoryMapping (BE1), rendered inside the
// @repo/ui Drawer by CategoryMappingModule. Mirrors DM2's conditional CHECK client-side:
// a 'Fee' mapping never carries a nominal code, a 'Nominal' mapping always requires one.
export const CategoryMappingForm = ({ category, mapping, accounts, onCancel, onSaved }: CategoryMappingFormProps) => {
  const { createCategoryMapping, updateCategoryMapping } = useHMRCITSAStore()

  const [sourceType, setSourceType] = useState<SourceType>(mapping?.sourceType || 'Fee')
  const [nominalOption, setNominalOption] = useState<NominalOption | null>(
    mapping?.nominalCode ? { value: mapping.nominalCode, label: mapping.nominalCode } : null
  )
  const [description, setDescription] = useState(mapping?.description || '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const nominalOptions: NominalOption[] = accounts.map((account) => ({
    value: account.accountId,
    label: account.accountName || account.accountId
  }))

  const isFormValid = sourceType === 'Fee' || Boolean(nominalOption?.value)

  const handleSave = async () => {
    setError('')
    setIsSaving(true)
    try {
      const payload = {
        hmrcMtdItsaCategoryId: category.hmrcMtdItsaCategoryId,
        sourceType,
        nominalCode: sourceType === 'Nominal' ? nominalOption?.value || null : null,
        description: description.trim() ? description.trim() : null
      }

      if (mapping) {
        await updateCategoryMapping({ hmrcMtdItsaCategoryMappingId: mapping.hmrcMtdItsaCategoryMappingId, ...payload })
      } else {
        await createCategoryMapping(payload)
      }

      await onSaved()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to save mapping. Please try again.')
      setIsSaving(false)
    }
  }

  return (
    <MUIStack data-testid="CategoryMappingForm" gap={4} sx={{ p: '0 24px 24px' }}>
      <MUITypography variant="subtitle1">{category.name}</MUITypography>

      {error && (
        <MUIAlert severity="error" data-testid="CategoryMappingFormError">
          {error}
        </MUIAlert>
      )}

      <MUIFormControl size="small" fullWidth>
        <MUIInputLabel id="category-mapping-source-type-label">Source Type</MUIInputLabel>
        <MUISelect
          labelId="category-mapping-source-type-label"
          label="Source Type"
          data-testid="CategoryMappingFormSourceTypeSelect"
          value={sourceType}
          disabled={isSaving}
          MenuProps={{ sx: { zIndex: DRAWER_OVERLAY_Z_INDEX } }}
          onChange={(event) => {
            setSourceType(event.target.value as SourceType)
            setNominalOption(null)
          }}
        >
          <MUIMenuItem value="Fee">Fee</MUIMenuItem>
          <MUIMenuItem value="Nominal">Nominal</MUIMenuItem>
        </MUISelect>
      </MUIFormControl>

      {sourceType === 'Fee' && (
        <MUITypography variant="caption" data-testid="CategoryMappingFormFeeHint" sx={{ color: 'text.secondary' }}>
          {`Fee mappings cover all fee income (the legacy '${LEGACY_FEE_SOURCE_LABEL}' ledger journal) — no nominal code required.`}
        </MUITypography>
      )}

      {sourceType === 'Nominal' && (
        <ReactSelectMenu
          label="Nominal Code"
          name="nominalCode"
          items={nominalOptions}
          value={nominalOption}
          onChange={(value: NominalOption | null) => setNominalOption(value)}
          disabled={isSaving}
          menuZindex={DRAWER_OVERLAY_Z_INDEX}
          required
        />
      )}

      <TextEntry
        name="description"
        label="Description"
        value={description}
        onChange={(value: string) => setDescription(value || '')}
        disabled={isSaving}
        multiline
      />

      <MUIStack direction="row" gap={2} justifyContent="flex-end">
        <MUIButton
          data-testid="CategoryMappingFormCancelButton"
          variant="outlined"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </MUIButton>
        <MUIButton
          data-testid="CategoryMappingFormSaveButton"
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
        >
          {isSaving ? <MUICircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
        </MUIButton>
      </MUIStack>
    </MUIStack>
  )
}
